import { Fragment } from "react";
import { Link, useLocation } from "@tanstack/react-router";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@monorepo-apps/ui/src/components/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@monorepo-apps/ui/src/components/collapsible";
import { ChevronRight, Compass, LogOut } from "lucide-react";

import { sidebarGroups } from "@/presentation/data/sidebar-items";
import { useAuth } from "@/presentation/providers/auth.provider";
import { useWorkspace } from "@/presentation/providers/workspace.provider";
import { WorkspaceSwitcher } from "@/presentation/components/shell/workspace-switcher";
import { useCurrentPhase } from "@/application/use-cases/phases/use-phases.queries";
import { useMeetingsList } from "@/application/use-cases/meetings/use-meetings.queries";

export function AppSidebar() {
  const location = useLocation();
  const { signOut, session } = useAuth();
  const { currentWorkspace, availableWorkspaces, isAdmin } = useWorkspace();
  const currentPhase = useCurrentPhase();
  const week = (currentPhase.data?.number ?? 1) as 1 | 2 | 3 | 4;
  const meetingsQ = useMeetingsList();
  const discoveries = (meetingsQ.data ?? [])
    .filter((m) => !!m.slug)
    .sort((a, b) => (a.meeting_at < b.meeting_at ? -1 : 1));
  const discoveriesActive = location.pathname.startsWith("/discoveries");

  return (
    <Sidebar collapsible="icon" data-slot="app-sidebar">
      <SidebarHeader className="border-sidebar-border gap-3 border-b px-5 py-5">
        <Link to="/" className="group flex items-center gap-3">
          <div className="ring-sidebar-border group-hover:ring-sidebar-primary/50 flex size-8 items-center justify-center overflow-hidden rounded-lg ring-1 transition-all">
            <img src="/favicon.png" alt="Yango" className="size-full object-cover" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="text-sidebar-foreground text-sm font-bold tracking-tight">
              Yango B2B
            </span>
            <span className="text-sidebar-foreground/40 text-[10px] font-medium tracking-widest uppercase">
              Concierge · W{week}/4
            </span>
          </div>
        </Link>
        <WorkspaceSwitcher />
      </SidebarHeader>

      <SidebarContent className="px-3 py-4">
        {sidebarGroups
          .filter(
            (group) =>
              group.workspace === currentWorkspace && availableWorkspaces.includes(group.workspace)
          )
          .map((group) => {
            const visibleItems = group.items.filter(
              (item) =>
                (!item.enabledFromWeek || item.enabledFromWeek <= week) &&
                (!item.adminOnly || isAdmin)
            );
            const isContext = group.label === "Context";
            if (visibleItems.length === 0 && !isContext) return null;
            const discoveriesAnchorIndex = isContext
              ? visibleItems.findIndex((i) => i.to === "/architecture/map")
              : -1;
            const discoveriesNode = isContext ? (
              <SidebarMenuItem key="__discoveries__">
                <Collapsible defaultOpen={discoveriesActive} className="group/collapsible">
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton tooltip="Discoveries">
                      <Compass className="size-4" />
                      <span className="text-[13px] font-medium">Discoveries</span>
                      <ChevronRight className="ml-auto size-3 transition-transform group-data-[state=open]/collapsible:rotate-90" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          asChild
                          isActive={location.pathname === "/discoveries"}
                        >
                          <Link to="/discoveries">
                            <span className="text-[12px]">All discoveries</span>
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      {discoveries.map((m) => {
                        const isActive = location.pathname === `/discoveries/${m.slug}`;
                        return (
                          <SidebarMenuSubItem key={m.id}>
                            <SidebarMenuSubButton asChild isActive={isActive}>
                              <Link to="/discoveries/$slug" params={{ slug: m.slug }}>
                                <span className="truncate text-[12px]">{m.title}</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        );
                      })}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </Collapsible>
              </SidebarMenuItem>
            ) : null;
            return (
              <SidebarGroup key={group.label}>
                <SidebarGroupLabel data-slot="sidebar-group-label">{group.label}</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu className="gap-1">
                    {visibleItems.map((item, index) => {
                      const pathNorm = location.pathname.replace(/\/$/, "") || "/";
                      const toNorm = item.to.replace(/\/$/, "") || "/";
                      const isActive =
                        item.to === "/"
                          ? location.pathname === "/"
                          : item.exact
                            ? pathNorm === toNorm
                            : pathNorm === toNorm || pathNorm.startsWith(`${toNorm}/`);
                      const Icon = item.icon;
                      return (
                        <Fragment key={item.to}>
                          {isContext && index === discoveriesAnchorIndex ? discoveriesNode : null}
                          <SidebarMenuItem>
                            <SidebarMenuButton asChild isActive={isActive} tooltip={item.label}>
                              <Link to={item.to}>
                                <Icon className="size-4" />
                                <span className="text-[13px] font-medium">{item.label}</span>
                              </Link>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        </Fragment>
                      );
                    })}
                    {isContext && discoveriesAnchorIndex === -1 ? discoveriesNode : null}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            );
          })}
      </SidebarContent>

      <SidebarFooter className="border-sidebar-border border-t px-3 py-3">
        <div className="flex items-center justify-between gap-2 px-2 pb-2">
          <div className="flex items-center gap-2">
            <span className="size-2 rounded-full bg-[var(--rag-green)]" />
            <span className="font-data text-sidebar-foreground/60 text-[11px] font-medium">
              System Online
            </span>
          </div>
        </div>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={() => void signOut()} tooltip="Sign out">
              <LogOut className="size-4" />
              <span className="text-sidebar-foreground/60 truncate text-[12px]">
                {session?.user.email ?? "Signed in"}
              </span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
